import { useState } from 'react';
import { LifeEvent } from '../hooks/useLifeEvents';
import { PanelBase } from './PanelBase';
import { TableViewBase } from './TableViewBase';
import { formatMoney } from 'src/common/currency';
import { showSuccess, showError } from 'src/toasts/toasts';
import { getFormattedDate } from '@mantine/dates';
import { LifeEventModal } from './LifeEventModal';
import { useDisclosure } from '@mantine/hooks';
import { useCurrency } from 'src/common/hooks/useCurrency';

type LifeEventPanelProps = {
  items: LifeEvent[];
  onSave: (data: LifeEvent[]) => Promise<void>;
  onDelete: (ids: string[]) => Promise<void>;
};

export function LifeEventPanel({ items = [], onSave, onDelete }: LifeEventPanelProps) {
  const currency = useCurrency();
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [selectedLifeEvent, setSelectedLifeEvent] = useState<LifeEvent | undefined>();

  const handleSave = async (lifeEvent: Omit<LifeEvent, 'id' | 'clientId'>) => {
    try {
      await onSave([{ ...lifeEvent, id: selectedLifeEvent?.id } as LifeEvent]);
      showSuccess('Life event saved successfully');
      closeModal();
      setSelectedLifeEvent(undefined);
    } catch {
      showError('Failed to save life event');
    }
  };

  const handleEdit = (lifeEvent: LifeEvent) => {
    setSelectedLifeEvent(lifeEvent);
    openModal();
  };

  const handleAdd = () => {
    setSelectedLifeEvent(undefined);
    openModal();
  };

  const handleDelete = (lifeEvent: LifeEvent) => {
    if (lifeEvent.id) {
      onDelete([lifeEvent.id]);
    }
  };

  const renderTableView = () => (
    <TableViewBase
      items={items}
      columns={[
        {
          key: 'description',
          header: 'Description',
          sortable: true,
          renderValue: value => {
            const str = String(value ?? '');
            return str.length > 50 ? `${str.slice(0, 50)}...` : str;
          },
        },
        {
          key: 'eventDate',
          header: 'Date',
          sortable: true,
          renderValue: value =>
            getFormattedDate({
              type: 'default',
              date: new Date(value),
              locale: 'en',
              format: 'D MMMM YYYY',
              labelSeparator: '',
            }),
        },
        { key: 'details', header: 'Details', sortable: true },
        {
          key: 'cost',
          header: 'Amount',
          sortable: true,
          renderValue: value => formatMoney(value, currency),
          align: 'right',
        },
      ]}
      onEdit={handleEdit}
      onDelete={handleDelete}
    />
  );

  return (
    <>
      <PanelBase<LifeEvent>
        items={items}
        addButtonLabel="Add a new life event"
        renderTableView={renderTableView}
        onAdd={handleAdd}
      />
      <LifeEventModal
        opened={modalOpened}
        onClose={closeModal}
        onSubmit={handleSave}
        initialValues={selectedLifeEvent}
      />
    </>
  );
}
